/* eslint-disable react-hooks/exhaustive-deps */
import { Autocomplete, Box, CircularProgress, Stack } from "@mui/material";
import { debounce } from "lodash";
import React, { SyntheticEvent } from "react";
import { api } from "../../../api/api";
import { removeDuplicateItems } from "../../../helpers/removeDuplicateItems";
import { initialProject } from "../../../redux/projects/defaultState";
import { IProject } from "../../../redux/projects/project.types";
import { CustomFormLabel, CustomTextField } from "../../formsComponents";
import { IProjectAutoSearchProps } from "./ProjectAutoSearch.types";
import axios from "axios";
import { useBillingCompanyFromStorage } from "../../../providers";

const INITIAL_STATE: IProject = initialProject;

export const ProjectAutoSearch: React.FC<IProjectAutoSearchProps> = (props) => {
  const {
    value,
    onSelect,
    label,
    hideAddButton,
    disabled,
    error,
    sx,
    placeholder,
  } = props;
  const [options, setOptions] = React.useState<readonly IProject[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearchText] = React.useState<string>("");
  const [openRightPanel, setOpenRightPanel] = React.useState<boolean>(false);
  const [hasFocused, setHasFocused] = React.useState(false);
  const customer = useBillingCompanyFromStorage();

  const fetchSuggestion = async (enteredValue: string, cancelToken: any) => {
    setLoading(true);
    try {
      let searchQuery = [];
      let billing_company_uuid = customer?.customer_uuid;

      if (billing_company_uuid) {
        searchQuery.push(`billing_company_uuid=${billing_company_uuid}`);
      }
      if (enteredValue.length > 0) {
        searchQuery.push(`columns=project_name&value=${enteredValue}`);
      }

      const res = await api.get(
        `project/get-project?${searchQuery.join("&")}&pageNo=1&itemPerPage=10`,
      );
      const data: IProject[] = res.data.data;
      setOptions(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const debounceFn = React.useCallback(debounce(fetchSuggestion, 800), []);

  const getOptionLabel = (option: IProject | string) => {
    if (typeof option === "string") return option || "";
    return option?.project_name;
  };

  const getValue = () => {
    const ans =
      options.find((option) => option.project_name === value.project_name) ||
      null;
    console.log("ans", ans);
    return (
      // options.find((option) => option.project_name === value.project_name) ||
      // null
      ans
    );
  };

  // const handleDropDownOnFocus = () => {
  // 	if (search.length === 0 && options.length === 0) {
  // 		debounceFn("");
  // 	}
  // };

  React.useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    if (search && search !== value.project_name) {
      debounceFn(search, cancelTokenSource.token);
    }
  }, [search]);

  React.useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    if (search && search.length > 0 && search !== value.project_name) {
      debounceFn(search, cancelTokenSource.token);
    }
  }, [search]);

  React.useEffect(() => {
    if (value && value.project_uuid?.length > 0) {
      const option: IProject = {
        ...INITIAL_STATE,
        project_uuid: value.project_uuid,
        project_name: value.project_name,
      };
      setOptions(
        removeDuplicateItems(
          [option, ...options],
          "project_uuid",
        ) as IProject[],
      );
    } else {
      setOptions([]);
    }
  }, [value]);

  React.useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();

    if (search && search !== value.project_name) {
      debounceFn(search, cancelTokenSource.token);
    }
    // Cleanup function to cancel the request when the component unmounts
    return () => {
      cancelTokenSource.cancel("Operation canceled due to component unmount.");
    };
  }, [search]);

  const handleFocus = () => {
    if (!hasFocused || search) {
      setHasFocused(true);
      const cancelTokenSource = axios.CancelToken.source();
      fetchSuggestion("", cancelTokenSource.token);

      // Cleanup on unmount
      return () => {
        cancelTokenSource.cancel(
          "Operation canceled due to component unmount.",
        );
      };
    }
  };

  return (
    <>
      {label && (
        <Box display="flex" justifyContent={"flex-start"} alignItems="center">
          <CustomFormLabel display={"block"}>{` ${label}  `}</CustomFormLabel>
        </Box>
      )}
      <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
        <Autocomplete
          id="project-auto-search"
          fullWidth
          freeSolo
          disabled={disabled}
          sx={{
            ".MuiOutlinedInput-root": {
              paddingTop: "2px",
              paddingBottom: "2px",
              fontSize: "0.8rem",
              color: "rgb(38, 38, 38)",
              width: "100%",
              backgroundColor: disabled ? "#f8f9fb" : "inherit",
            },
          }}
          onFocus={handleFocus}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, value) =>
            typeof option !== "string" && typeof value !== "string"
              ? option.project_name === value.project_name
              : option === value
          }
          filterOptions={(x) => x}
          options={options}
          autoComplete
          includeInputInList
          // onFocus={handleDropDownOnFocus}
          value={getValue()}
          noOptionsText="No Data"
          onChange={(
            event: React.SyntheticEvent<Element, Event>,
            value: string | IProject | null,
          ) => {
            if (value) {
              onSelect(value as IProject);
            } else {
              onSelect(initialProject);
            }
          }}
          onInputChange={(event, newInputValue) => {
            if ((event && event.type === "change") || !newInputValue) {
              setSearchText(newInputValue);
            }
            if (newInputValue === "") {
              onSelect(initialProject);
            }
          }}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              fullWidth
              placeholder="Search by Project"
              sx={{
                ...sx,
                "& .MuiOutlinedInput-root": {
                  paddingTop: "2px",
                  paddingBottom: "2px",
                  fontSize: "0.8rem",
                  color: "rgb(38, 38, 38)",
                  width: "100%",
                  backgroundColor: disabled ? "#f8f9fb" : "inherit",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              disabled={disabled}
              error={error ? true : false}
              helperText={error}
            />
          )}
        />
      </Stack>
    </>
  );
};
